// traceability: requirements: [FR-017, NFR-008]
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { clsx } from 'clsx';
import { useAuth } from '@/hooks/useAuth';
import { useTheme } from '@/hooks/useTheme';
import { LanguageSwitcher } from './LanguageSwitcher';
import { Button } from '../ui/Button';

export function Header() {
  const { t } = useTranslation();
  const { isAuthenticated, user, isAdmin, logout } = useAuth();
  const { mode, setMode } = useTheme();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { to: '/', label: t('nav.home') },
    { to: '/questionnaire', label: t('nav.questionnaires') },
    ...(isAuthenticated
      ? [
          { to: '/analysis', label: t('nav.analysis') },
          { to: '/booking', label: t('nav.booking') },
          { to: '/my-bookings', label: t('nav.myBookings') },
        ]
      : []),
    ...(isAdmin ? [{ to: '/admin', label: t('nav.admin') }] : []),
  ];

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <header className="sticky top-0 z-40 border-b border-zinc-200 bg-white/80 backdrop-blur-md dark:border-zinc-800 dark:bg-zinc-950/80">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-8">
          <Link to="/" className="flex items-center gap-2 text-jade-700 dark:text-jade-400" aria-label={t('app.name')}>
            <svg width="28" height="28" viewBox="0 0 32 32" fill="currentColor">
              <circle cx="16" cy="16" r="15" fill="none" stroke="currentColor" strokeWidth="2" />
              <path d="M16 1C16 1 16 16 16 16C16 16 1 16 1 16C1 7.716 7.716 1 16 1Z" />
              <circle cx="11" cy="11" r="3" fill="currentColor" className="text-white dark:text-zinc-950" />
              <circle cx="21" cy="21" r="3" />
            </svg>
            <span className="hidden font-heading text-lg font-bold sm:block">{t('app.name')}</span>
          </Link>

          <nav className="hidden items-center gap-1 md:flex" aria-label="Main navigation">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={clsx(
                  'rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                  isActive(link.to)
                    ? 'bg-jade-50 text-jade-700 dark:bg-jade-900/30 dark:text-jade-400'
                    : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-200',
                )}
                aria-current={isActive(link.to) ? 'page' : undefined}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-2">
          <LanguageSwitcher />

          <button
            onClick={() => setMode(mode === 'dark' ? 'light' : mode === 'light' ? 'system' : 'dark')}
            className="rounded-lg p-2 text-zinc-500 hover:bg-zinc-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-jade-500 dark:text-zinc-400 dark:hover:bg-zinc-800"
            aria-label={t('common.theme')}
          >
            {mode === 'dark' ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></svg>
            ) : mode === 'light' ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" /><line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" /><line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" /><line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" /></svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></svg>
            )}
          </button>

          {isAuthenticated ? (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button
                  className="flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-jade-500 dark:text-zinc-300 dark:hover:bg-zinc-800"
                  aria-label="User menu"
                >
                  {user?.avatarUrl ? (
                    <img src={user.avatarUrl} alt="" className="h-7 w-7 rounded-full object-cover" />
                  ) : (
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-jade-100 text-xs font-semibold text-jade-700 dark:bg-jade-900 dark:text-jade-300">
                      {user?.firstName?.[0]}{user?.lastName?.[0]}
                    </span>
                  )}
                  <span className="hidden sm:block">{user?.firstName}</span>
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  className="z-50 min-w-[180px] rounded-lg border border-zinc-200 bg-white p-1 shadow-lg dark:border-zinc-700 dark:bg-zinc-900"
                  sideOffset={4}
                  align="end"
                >
                  <DropdownMenu.Item asChild>
                    <Link
                      to="/profile"
                      className="flex cursor-pointer items-center rounded-md px-3 py-2 text-sm text-zinc-700 outline-none hover:bg-zinc-50 focus:bg-zinc-50 dark:text-zinc-300 dark:hover:bg-zinc-800"
                    >
                      {t('nav.profile')}
                    </Link>
                  </DropdownMenu.Item>
                  <DropdownMenu.Separator className="my-1 h-px bg-zinc-100 dark:bg-zinc-800" />
                  <DropdownMenu.Item
                    className="flex cursor-pointer items-center rounded-md px-3 py-2 text-sm text-cinnabar-600 outline-none hover:bg-cinnabar-50 focus:bg-cinnabar-50 dark:text-cinnabar-400 dark:hover:bg-cinnabar-900/20"
                    onSelect={logout}
                  >
                    {t('nav.logout')}
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          ) : (
            <div className="hidden items-center gap-2 sm:flex">
              <Button variant="ghost" size="sm" asChild>
                <Link to="/login">{t('nav.login')}</Link>
              </Button>
              <Button variant="primary" size="sm" asChild>
                <Link to="/register">{t('nav.register')}</Link>
              </Button>
            </div>
          )}

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="rounded-lg p-2 text-zinc-500 hover:bg-zinc-100 focus:outline-none focus-visible:ring-2 md:hidden dark:text-zinc-400 dark:hover:bg-zinc-800"
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {mobileMenuOpen ? (
                <path d="M6 6l12 12M18 6L6 18" />
              ) : (
                <path d="M3 12h18M3 6h18M3 18h18" />
              )}
            </svg>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <nav className="border-t border-zinc-200 bg-white px-4 py-3 md:hidden dark:border-zinc-800 dark:bg-zinc-950" aria-label="Mobile navigation">
          <div className="flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={() => setMobileMenuOpen(false)}
                className={clsx(
                  'rounded-lg px-3 py-2 text-sm font-medium',
                  isActive(link.to)
                    ? 'bg-jade-50 text-jade-700 dark:bg-jade-900/30 dark:text-jade-400'
                    : 'text-zinc-600 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-800',
                )}
              >
                {link.label}
              </Link>
            ))}
            {!isAuthenticated && (
              <div className="mt-2 flex flex-col gap-2 border-t border-zinc-100 pt-2 dark:border-zinc-800">
                <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="rounded-lg px-3 py-2 text-sm font-medium text-zinc-600 hover:bg-zinc-100 dark:text-zinc-400">
                  {t('nav.login')}
                </Link>
                <Link to="/register" onClick={() => setMobileMenuOpen(false)} className="rounded-lg px-3 py-2 text-sm font-medium text-jade-700 dark:text-jade-400">
                  {t('nav.register')}
                </Link>
              </div>
            )}
          </div>
        </nav>
      )}
    </header>
  );
}
